import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'antd';

interface MultiLineComponentProps {
	children: React.ReactNode;
	lines?: number;
	lineHeight?: number;
	style?: React.CSSProperties;
}

const MultiLineComponent: FC<MultiLineComponentProps> = ({
	children,
	lines = 2,
	lineHeight = 32,
	style,
}): React.ReactElement => {
	const contentRef = useRef<HTMLDivElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);
	const [expanded, setExpanded] = useState(false);

	const maxHeight = lines * lineHeight;

	const checkOverflow = useCallback(() => {
		const el = contentRef.current;
		if (el) {
			// Temporarily remove clamp to measure full height
			const prev = el.style.maxHeight;
			el.style.maxHeight = 'none';
			const fullHeight = el.scrollHeight;
			el.style.maxHeight = prev;
			setIsOverflowing(fullHeight > maxHeight);
		}
	}, [maxHeight]);

	useEffect(() => {
		// Use requestAnimationFrame to ensure DOM has rendered before measuring
		const id = requestAnimationFrame(checkOverflow);
		return () => cancelAnimationFrame(id);
	}, [checkOverflow, children]);

	useEffect(() => {
		window.addEventListener('resize', checkOverflow);
		return () => {
			window.removeEventListener('resize', checkOverflow);
		};
	}, [checkOverflow]);

	const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);

	return (
		<div style={style}>
			<div
				ref={contentRef}
				style={
					expanded
						? undefined
						: {
								maxHeight: maxHeight,
								overflow: 'hidden',
						  }
				}
			>
				{children}
			</div>
			{(isOverflowing || expanded) && (
				<Button onClick={toggleExpanded} type="link" style={{ padding: 0, lineHeight: 1 }}>
					{expanded ? 'See less' : 'See more'}
				</Button>
			)}
		</div>
	);
};

export default MultiLineComponent;
